import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "zh";
const STORAGE_KEY = "axiom-lang";

type Dict = Record<string, string>;
type Replacements = Record<string, string | number>;

/**
 * Platinum bilingual dictionary. Every operator-visible string in the app
 * is keyed here. Two ground rules:
 *   1. EN mode must read as native English — no Chinese remnants.
 *   2. ZH mode must read as native Mandarin — no inert English carrying
 *      semantic load (proper nouns like "Oracle" / "4SAPI" stay as-is).
 */
const DICT: Record<Lang, Dict> = {
  en: {
    // ── Brand & nav
    "brand.tagline": "Personal Decision Intelligence",
    "nav.section.sandbox": "Sandbox",
    "nav.section.intelligence": "Intelligence",
    "nav.section.knowledge": "Knowledge",
    "nav.section.system": "System",
    "nav.dashboard": "Dashboard",
    "nav.dashboard.hint": "Capital & runway",
    "nav.projects": "Project Sandbox",
    "nav.projects.hint": "Active arena",
    "nav.decisions": "Decision Audit",
    "nav.decisions.hint": "Audit log",
    "nav.ledger": "Capital Ledger",
    "nav.ledger.hint": "Income & expense",
    "nav.insights": "Strategic Insights",
    "nav.insights.hint": "McKinsey board",
    "nav.vault": "Knowledge Vault",
    "nav.vault.hint": "Strategy docs",
    "nav.oracle": "Oracle Briefings",
    "nav.oracle.hint": "AI brief",
    "nav.settings": "Settings",
    "footer.version": "v4.1 · sandbox",

    // ── Shell
    "shell.status.live": "Live",
    "shell.status.sync": "Sync",
    "shell.status.fail": "Sync failed",
    "shell.signout": "Sign out",
    "shell.auth.checking": "Verifying session…",
    "shell.menu.open": "Open menu",
    "shell.menu.close": "Close menu",
    "shell.theme.toggle.light": "Switch to light mode",
    "shell.theme.toggle.dark": "Switch to dark mode",
    "shell.lang.toggle": "Switch language",
    "shell.console": "Console",
    "sidebar.search.placeholder": "Jump to module…",
    "sidebar.navigation": "Primary navigation",
    "sidebar.console": "Console",
    "sidebar.domains": "Domains · 9",
    "sidebar.sqlite": "SQLite Tables",
    "sidebar.pool": "4SAPI Pool",
    "sidebar.build": "Build",
    "sidebar.os": "Sovereign OS",

    // ── Common
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.create": "Create",
    "common.loading": "Loading…",
    "common.search": "Search",
    "common.untitled": "Untitled",

    // ── Dashboard
    "dashboard.eyebrow": "Command Deck",
    "dashboard.title": "Capital snapshot",
    "dashboard.desc":
      "Net position, monthly flow, and distance to the absolute floor. Every entry below feeds the next decision.",
    "dashboard.action.log": "Log entry",
    "dashboard.net.title": "Net Capital Position",
    "dashboard.net.baseline": "Baseline {x} CNY · since {date}",
    "dashboard.net.formula": "Baseline + Σ(income) − Σ(expense)",
    "dashboard.flow.title": "Monthly Burn Rate (30D)",
    "dashboard.flow.split": "+{inflow} / −{outflow}",
    "dashboard.runway.title": "Capital Runway",
    "dashboard.runway.unit": "{x} mo",
    "dashboard.runway.unlimited": "∞ mo",
    "dashboard.runway.floor": "Floor {x} CNY",
    "dashboard.headroom.title": "Financial Headroom",
    "dashboard.headroom.hint": "Distance to absolute danger line",
    "dashboard.status.stable": "Cash-flow stable",
    "dashboard.status.watch": "Watch burn",
    "dashboard.status.risk": "Floor risk",
    "dashboard.status.loading": "Loading",
    "dashboard.spark.hint": "30-day rolling net position. Last point is today.",
    "dashboard.projects.title": "Active projects",
    "dashboard.projects.subtitle": "{active} active / {total} total",
    "dashboard.projects.loading": "Loading projects…",
    "dashboard.projects.empty.title": "No active projects",
    "dashboard.projects.empty.hint": "Open the arena to add one.",
    "dashboard.projects.open": "Open sandbox",
    "dashboard.decisions.title": "Open decisions",
    "dashboard.decisions.subtitle": "{open} open / {total} logged",
    "dashboard.decisions.loading": "Loading decisions…",
    "dashboard.decisions.empty.title": "No open decisions",
    "dashboard.decisions.empty.hint": "Log one when you face a non-obvious choice.",
    "dashboard.decisions.open": "Open log",
    "dashboard.decisions.optsOne": "{n} option · {preview}",
    "dashboard.decisions.optsMany": "{n} options · {preview}",
    "dashboard.tx.title": "Recent transactions",
    "dashboard.tx.subtitle": "Last 8 ledger entries. Open the Ledger view for the full history.",
    "dashboard.tx.open": "Open ledger",
    "dashboard.tx.empty.title": "No transactions yet",
    "dashboard.tx.empty.hint": "Log income or expense above to start the curve.",
    "dashboard.quick.title": "Log a transaction",
    "dashboard.quick.desc": "One line. This updates the runway calculation instantly.",
    "dashboard.quick.outflow": "Outflow",
    "dashboard.quick.inflow": "Inflow",
    "dashboard.quick.expense": "Expense",
    "dashboard.quick.income": "Income",
    "dashboard.quick.amount": "Amount (CNY)",
    "dashboard.quick.amount.ph": "e.g. 250",
    "dashboard.quick.note": "Note",
    "dashboard.quick.note.ph": "What for?",
    "dashboard.quick.confirm": "Log",
    "dashboard.quick.submitting": "Logging…",
    "dashboard.quick.amount.invalid": "Amount must be > 0",
    "dashboard.quick.toast": "{kind} recorded · {x} CNY",
    "dashboard.quick.fail": "Failed to record transaction",
    "dashboard.floor.title": "Below absolute floor",
    "dashboard.floor.desc":
      "Net position has crossed the configured floor of {x} CNY. Halt commits that increase burn until inflow recovers.",
    "dashboard.fetch.fail": "Dashboard fetch failed",
    "dashboard.kpi.netWorth": "Net Worth",
    "dashboard.kpi.vs30": "vs. 30 days ago · {amount}",
    "dashboard.kpi.runway": "Capital Runway",
    "dashboard.kpi.days": "days",
    "dashboard.kpi.positiveCashflow": "Positive cash flow",
    "dashboard.kpi.runwayEstimate": "Until {date} · monthly burn ¥{amount}",
    "dashboard.kpi.decisions": "Decision Outcomes",
    "dashboard.kpi.records": "{n} records",
    "dashboard.kpi.reviewed": "{n} reviews closed · rolling 12 months",
    "dashboard.kpi.awaitingReview": "Awaiting first review",
    "dashboard.kpi.tokens": "4SAPI · Token Pool",
    "dashboard.kpi.healthy": "Healthy",
    "dashboard.kpi.live": "Live",
    "dashboard.kpi.gateway": "gateway",
    "dashboard.kpi.telemetryPending": "27 keys · 6 providers · telemetry pending",
    "dashboard.runwayChart.title": "Capital Runway Forecast",
    "dashboard.chart.method": "Measures",
    "dashboard.runwayChart.empty": "Set a baseline and record one transaction to generate the runway forecast.",
    "dashboard.runwayChart.subtitle.positive": "Net capital projected against the {floor} CNY floor · positive cash flow",
    "dashboard.runwayChart.subtitle.days": "Net capital projected against the {floor} CNY floor · {days} days remaining",
    "dashboard.runwayChart.active": "Active capital",
    "dashboard.runwayChart.cash": "Net capital",
    "dashboard.runwayChart.toFloor": "Days to floor",
    "dashboard.runwayChart.monthlyBurn": "Monthly burn",
    "dashboard.runwayChart.floor": "Capital floor",
    "dashboard.runwayChart.estimate": "Burn estimate",
    "dashboard.runwayChart.moving": "30-day moving estimate · live",
    "dashboard.runwayChart.floorLabel": "FLOOR · ¥{amount}",
    "dashboard.runwayChart.now": "TODAY · {date}",
    "dashboard.runwayChart.actual": "ACTUAL · {date}",
    "dashboard.runwayChart.forecast": "FORECAST · {date}",
    "dashboard.runwayChart.point.aria": "Inspect capital position on {date}",
    "dashboard.runwayChart.exhaustPositive": "∞ · positive cash flow",
    "dashboard.runwayChart.exhaustDate": "Floor {date} · {days}d",
    "dashboard.matrix.title": "Risk × Return Positioning Matrix",
    "dashboard.matrix.subtitle": "{count} active projects · bubble area = committed capital · color = allocation posture",
    "dashboard.matrix.empty": "No active capital commitments yet. Add a project to populate the matrix.",
    "dashboard.matrix.q.ideal": "Priority allocation",
    "dashboard.matrix.q.hero": "Strategic bet",
    "dashboard.matrix.q.chore": "Steady yield",
    "dashboard.matrix.q.trap": "Exit review",
    "dashboard.matrix.desc.ideal": "Lower risk × higher return",
    "dashboard.matrix.desc.hero": "Higher risk × higher return",
    "dashboard.matrix.desc.chore": "Lower risk × lower return",
    "dashboard.matrix.desc.trap": "Higher risk × lower return",
    "dashboard.matrix.axis.risk": "RISK EXPOSURE",
    "dashboard.matrix.axis.roi": "EXPECTED RETURN",
    "dashboard.matrix.axis.low": "low",
    "dashboard.matrix.axis.high": "high",
    "dashboard.matrix.tooltip.project": "PROJECT",
    "dashboard.matrix.tooltip.capital": "Capital · ¥{amount}",
    "dashboard.matrix.tooltip.expected": "Return · {value}×",
    "dashboard.matrix.point.aria": "Inspect project {project}",
    "dashboard.cashflow.title": "Cash-flow Pulse",
    "dashboard.cashflow.activity": "Daily inflows and outflows over 30 days · {days} active days · net ¥{net}",
    "dashboard.cashflow.pending": "Daily inflows and outflows over 30 days · awaiting first transaction",
    "dashboard.cashflow.in": "Inflow",
    "dashboard.cashflow.out": "Outflow",
    "dashboard.cashflow.empty": "No cash-flow activity in the past 30 days. Record an inflow or outflow to activate this chart.",
    "dashboard.cashflow.in30": "30D inflow",
    "dashboard.cashflow.out30": "30D outflow",
    "dashboard.cashflow.net": "Net flow",
    "dashboard.cashflow.largest": "Largest day",
    "dashboard.cashflow.bar.aria": "Inspect cash flow on {date}",
    "dashboard.cashflow.selected": "Selected · {date}",
    "dashboard.cashflow.selected.in": "Inflow +¥{value}",
    "dashboard.cashflow.selected.out": "Outflow -¥{value}",
    "dashboard.cashflow.selected.net": "Net ¥{value}",
    "dashboard.domain.title": "Nine-Domain Activity Audit",
    "dashboard.domain.subtitle": "Recorded activity over the past 30 days · max {max}",
    "dashboard.domain.window": "30d · {count} actions",
    "dashboard.domain.missing": "Inactive",
    "dashboard.domain.hot": "Concentrated",
    "dashboard.domain.balance": "Balance",
    "dashboard.funnel.title": "Decision Conversion Funnel",
    "dashboard.funnel.subtitle": "Signals → decisions → reviewed wins · rolling 12 months",
    "dashboard.funnel.observations": "SIGNALS CAPTURED",
    "dashboard.funnel.decisions": "DECISIONS MADE",
    "dashboard.funnel.wins": "POSITIVE REVIEWS",
    "dashboard.funnel.rate": "Reviewed win rate",
    "dashboard.funnel.rolling": "{wins} / {reviewed} · rolling 12 months",
    "dashboard.funnel.best": "Best domain",
    "dashboard.funnel.worst": "Lowest domain",
    "dashboard.allocation.title": "Capital Allocation · MECE",
    "dashboard.allocation.subtitle": "¥{amount} · mutually exclusive, collectively exhaustive",
    "dashboard.allocation.empty": "No deployed capital to allocate.",
    "dashboard.allocation.tag.ideal": "Priority",
    "dashboard.allocation.tag.hedged": "Balanced",
    "dashboard.allocation.tag.hero": "Strategic",
    "dashboard.allocation.tag.speculative": "Speculative",
    "dashboard.allocation.reserve": "Liquidity reserve",
    "dashboard.allocation.sunk": "Sunk capital",
    "dashboard.ledger.title": "Recent Decision Ledger",
    "dashboard.ledger.subtitle": "Written to SQLite decisions · lifecycle tracking active",
    "dashboard.ledger.filter.all": "All",
    "dashboard.ledger.filter.open": "Open",
    "dashboard.ledger.filter.reviewed": "Reviewed",
    "dashboard.ledger.column.decision": "Decision",
    "dashboard.ledger.column.domain": "Domain",
    "dashboard.ledger.column.options": "Options",
    "dashboard.ledger.column.outcome": "Outcome",
    "dashboard.ledger.column.state": "State",
    "dashboard.ledger.empty": "No decisions recorded yet",
    "dashboard.ledger.state.shipped": "Positive",
    "dashboard.ledger.state.loss": "Negative",
    "dashboard.ledger.state.reviewed": "Reviewed",
    "dashboard.ledger.state.committed": "Committed",
    "dashboard.ledger.state.open": "Open",
    "dashboard.principles.title": "Principles Audit",
    "dashboard.principles.subtitle": "Triggers this month · 0 breaches",
    "dashboard.principles.zero": "0 breaches",
    "dashboard.principles.next": "Next audit",
    "dashboard.principles.nextDetail": "Sunday routine · #03 threshold review",
    "dashboard.principles.static": "Illustrative until the principles table supplies live data",
    "dashboard.principles.01": "Never draw from the emergency reserve",
    "dashboard.principles.03": "Single-project exposure ≤ 50% of cash",
    "dashboard.principles.03trigger": "42% near limit",
    "dashboard.principles.05": "Do not commit when ROI < 3×",
    "dashboard.principles.07": "Reject outsourcing that compromises autonomy",
    "dashboard.principles.09": "Complete at least one review each week",
    "dashboard.principles.11": "Debt ≤ one third of annual net cash flow",
    "dashboard.footer.release": "Axiom Core 3.0 · sovereign mode",
    "dashboard.footer.tables": "SQLite · 4 tables · ledger / projects / decisions / capital_tx",
    "dashboard.footer.gateway": "4SAPI gateway · multiple providers",
    "dashboard.footer.nominal": "All systems nominal",
    "dashboard.footer.build": "build {date}",

    // ── Omni Command Bar (NLP ingestion)
    "omni.placeholder":
      "Dump today's business progress, capital flows, decisions, or reflections here for automatic AI auditing…",
    "omni.send": "Send",
    "omni.sending": "Auditing & archiving stream active…",
    "omni.streaming": "Auditing & archiving stream active…",
    "omni.success": "Data successfully audited by AI, dispatched, and permanently archived.",
    "omni.hint":
      "Press Enter to log. The model writes to capital_tx / decisions / projects, or appends Markdown to the matching domain README.",
    "omni.toast.ok": "Archived",
    "omni.toast.fail": "Audit failed",
    "omni.console.eyebrow": "Core Command Console",
    "omni.console.title": "Audit any commercial event in one line",
    "omni.console.subtitle":
      "Cashflow, decision, project, principle — drop it raw, the model files it into the right slot.",
    "omni.console.provider": "Provider",
    "omni.console.model": "Inference Engine",
    "omni.console.model.default": "Server default",
    "omni.console.model.empty": "Configure in Oracle first",
    "omni.console.provider.empty": "Select provider",
    "omni.console.provider.placeholder": "Select provider",
    "omni.console.engine.empty": "No engines available",
    "omni.console.engine.placeholder": "Select engine",
    "omni.console.engine.locked": "Select a provider first",
    "omni.console.cascade.aria": "Inference engine selector",

    // ── Providers
    "provider.anthropic": "Anthropic (Claude)",
    "provider.openai": "OpenAI (ChatGPT)",
    "provider.google": "Google (Gemini)",
    "provider.deepseek": "DeepSeek",
    "provider.other": "Other",

    // ── Projects
    "projects.matrix.title": "McKinsey Project Strategy Matrix (Risk-ROI Matrix)",
    "projects.matrix.subtitle": "Risk × Return positioning · bubble size = capital committed",
    "projects.matrix.axisY": "ROI Projection (×)",
    "projects.matrix.axisX": "Risk level",
    "projects.matrix.empty": "No active projects in sandbox. Matrix chassis remains active for control observation.",
    "projects.matrix.quadrant.q1": "Star bets",
    "projects.matrix.quadrant.q2": "Speculative",
    "projects.matrix.quadrant.q3": "Defensive",
    "projects.matrix.quadrant.q4": "Drains",
    "projects.matrix.tooltip.roi": "ROI",
    "projects.matrix.tooltip.committed": "Committed",
    "projects.matrix.tooltip.spent": "Spent",
    "projects.eyebrow": "Sandbox",
    "projects.title": "Project Sandbox",
    "projects.desc":
      "The commercial bets currently in flight. Each row carries its investment thesis, ROI projection, risk level, and hard kill criteria.",
    "projects.action.new": "New project",
    "projects.empty.title": "No projects yet",
    "projects.empty.hint": "Open “New project” to record your first bet.",
    "projects.kill": "Hard kill if:",
    "projects.risk.suffix": "{level} risk",
    "projects.roi": "ROI Projection {x}x",
    "projects.spent": "Capital Committed {spent} / {committed} CNY",
    "projects.confirm.delete": "Delete “{name}”? This cannot be undone.",
    "projects.toast.created": "Project added",
    "projects.toast.updated": "Project updated",
    "projects.toast.deleted": "Project deleted",
    "projects.toast.saveFail": "Save failed",
    "projects.toast.deleteFail": "Delete failed",
    "projects.toast.loadFail": "Failed to load projects",
    "projects.form.title.new": "New project",
    "projects.form.title.edit": "Edit project",
    "projects.form.desc":
      "Name and thesis are mandatory in the operator's head; the form only checks name.",
    "projects.form.name": "Name",
    "projects.form.name.required": "Name is required",
    "projects.form.name.ph": "e.g. Cross-border supply chain dropship",
    "projects.form.thesis": "Investment Thesis & Assumptions",
    "projects.form.thesis.ph": "Why this should work.",
    "projects.form.status": "Status",
    "projects.form.risk": "Risk",
    "projects.form.roi": "ROI Projection (×)",
    "projects.form.committed": "Capital Committed (CNY)",
    "projects.form.kill": "Hard Kill Criteria",
    "projects.form.kill.ph": "What signal, if observed, kills this project?",
    "projects.status.active": "active",
    "projects.status.paused": "paused",
    "projects.status.killed": "killed",
    "projects.status.shipped": "shipped",
    "projects.metrics.active": "Active",
    "projects.metrics.live": "{count} live",
    "projects.metrics.roi": "Return",
    "projects.metrics.mean": "Portfolio mean",
    "projects.metrics.committed": "Committed",
    "projects.metrics.allocated": "CNY allocated",
    "projects.metrics.spent": "Consumed",
    "projects.metrics.consumed": "CNY consumed",
    "projects.badge.star": "Priority allocation",
    "projects.badge.open": "Open",
    "projects.kpi.return": "ROI",
    "projects.kpi.capital": "Capital",
    "risk.low": "low",
    "risk.medium": "medium",
    "risk.high": "high",
    "risk.extreme": "extreme",

    // ── Domains (DomainSelect primitive)
    "domain.label": "Archive to domain",
    "domain.placeholder": "Unfiled (can be tagged later)",
    "domain.name.01": "Health & Energy",
    "domain.name.02": "Cash Flow",
    "domain.name.03": "Career",
    "domain.name.04": "Skills",
    "domain.name.05": "Projects",
    "domain.name.06": "Cognition",
    "domain.name.07": "Relationships",
    "domain.name.08": "Decisions",
    "domain.name.09": "Principles",

    // ── Strategic Insights (McKinsey board)
    "insights.eyebrow": "Strategic Insights",
    "insights.title": "Strategic Insights Board",
    "insights.desc":
      "Six McKinsey-grade charts on a single canvas — risk-return positioning, runway velocity, capital allocation, decision audit, exposure concentration, and cross-domain energy distribution.",
    "insights.toast.loadFail": "Failed to load insights data",

    "insights.matrix.title": "Risk × Return Strategy Matrix",
    "insights.matrix.subtitle": "Bubble size = capital committed · click to jump to the row",
    "insights.matrix.empty": "No projects yet. Charts wake up after your first bet.",
    "insights.matrix.axisX": "Risk level",
    "insights.matrix.axisY": "ROI projection (×)",
    "insights.matrix.q.star": "Star Bets",
    "insights.matrix.q.speculative": "Speculative",
    "insights.matrix.q.defensive": "Defensive",
    "insights.matrix.q.drains": "Drains",
    "insights.matrix.tooltip.roi": "ROI",
    "insights.matrix.tooltip.committed": "Capital",

    "insights.runway.title": "Capital Runway & Convergence",
    "insights.runway.subtitle": "Historical curve · forecast trajectory · absolute floor at −100k CNY",
    "insights.runway.empty": "Set a baseline and a transaction to draw the runway.",
    "insights.runway.axisX": "Days (history → forecast)",
    "insights.runway.today": "Today",
    "insights.runway.floor": "Floor",
    "insights.runway.kpi.net": "Net Position",
    "insights.runway.kpi.velocity": "Monthly Velocity",
    "insights.runway.kpi.survival": "Survival",
    "insights.runway.survival.infinite": "∞ days",
    "insights.runway.survival.breached": "Floor breached",
    "insights.runway.survival.days": "{n} days left",

    "insights.allocation.title": "Capital Allocation Audit",
    "insights.allocation.subtitle": "MECE stacked bars · committed vs. consumed per project",
    "insights.allocation.empty": "No live capital commitments yet.",
    "insights.allocation.column.project": "Project",
    "insights.allocation.column.usage": "Consumption %",
    "insights.allocation.overrun": "Overrun",

    "insights.funnel.title": "Decision Audit Funnel",
    "insights.funnel.subtitle": "Open → Committed → Reviewed · honest win rate",
    "insights.funnel.empty": "No decisions logged yet.",
    "insights.funnel.stage.open": "Open",
    "insights.funnel.stage.committed": "Committed",
    "insights.funnel.stage.reviewed": "Reviewed",
    "insights.funnel.successRate": "Reviewed Win Rate",
    "insights.funnel.successRate.pending": "Awaiting reviews",
    "insights.funnel.foot.positive": "Positive outcomes",
    "insights.funnel.foot.negative": "Negative outcomes",
    "insights.funnel.foot.total": "Total entries",

    "insights.exposure.title": "Risk Exposure Concentration",
    "insights.exposure.subtitle": "Aggregate capital share per risk tier · alert threshold 40%",
    "insights.exposure.empty": "No live capital to weigh.",
    "insights.exposure.threshold": "High + Extreme",
    "insights.exposure.alert.title": "Concentration alert",
    "insights.exposure.alert.detail":
      "High and Extreme risk tiers now hold {pct}% of live capital — above the 40% reorganisation threshold.",
    "insights.exposure.watch": "High-risk allocation at {pct}% — approaching the 40% reorganisation threshold.",

    "insights.waterfall.title": "Domain Energy Waterfall",
    "insights.waterfall.subtitle": "30-day operation frequency across the nine domains",
    "insights.waterfall.empty": "Domain registry is empty — configure domains first.",
    "insights.waterfall.silent": "No tagged activity in the last 30 days.",
    "insights.waterfall.axisX": "Domains",
    "insights.waterfall.axisY": "Operations (count)",
    "insights.waterfall.foot": "{total} tagged operations across the last {window} days.",
    "insights.governance.status": "Governance engine active",
    "insights.governance.theme": "Theme [{mode}]",
    "insights.governance.dark": "dark",
    "insights.governance.light": "light",
    "insights.governance.metric.net": "Net Capital Capacity",
    "insights.governance.metric.audit": "Compliance Audit Rate",
    "insights.governance.metric.velocity": "Gateway Token Throughput",
    "insights.governance.metric.stable": "Stable",
    "insights.governance.metric.descriptor": "Current metric",
    "insights.governance.chart": "Financial Capacity Trend",
    "insights.governance.realtimeGraph": "Real-time graph",
    "insights.governance.tooltip": "VALUE: $742,019",
    "insights.governance.register": "System Transactions Register",
    "insights.governance.live": "Live · 200",
    "insights.governance.column.index": "Index",
    "insights.governance.column.node": "Service Node",
    "insights.governance.column.code": "Status Code",
    "insights.governance.column.latency": "Latency",
    "insights.governance.audit": "Governance Control Audit",
    "insights.governance.realtime": "Real-time",
    "insights.governance.integrity": "SQLite structural integrity",
    "insights.governance.secure": "100% secure",
    "insights.governance.isolation": "Network isolation filter",
    "insights.governance.active": "Active",
    "insights.governance.sync": "WPS / Dropbox synchronization lock",
    "insights.governance.hold": "On hold",
    "insights.governance.journal.title": "Immutable Telemetry Journal",
    "insights.governance.journal.badge": "Journal",
    "insights.governance.log.01": "Base-link optimization verification completed.",
    "insights.governance.log.02": "Dynamic routing rules were re-anchored through 4SAPI.",
    "insights.governance.log.03": "Enterprise synchronization lock refreshed successfully.",
    "insights.governance.log.04": "Token depletion-curve convergence check passed.",

    // ── Decisions
    "decisions.eyebrow": "Sandbox",
    "decisions.title": "Decision Audit",
    "decisions.desc":
      "Every meaningful choice becomes a row here. Capture the context, options, rationale, and expected outcome — review later, honestly.",
    "decisions.action.new": "Log decision",
    "decisions.bucket.open": "Open",
    "decisions.bucket.committed": "Committed",
    "decisions.bucket.reviewed": "Reviewed",
    "decisions.bucket.count.one": "{n} entry",
    "decisions.bucket.count.other": "{n} entries",
    "decisions.empty": "Empty",
    "decisions.empty.hint": "Log decisions as they appear.",
    "decisions.row.choice": "Choice: {choice}",
    "decisions.row.expected": "Expected: {x}",
    "decisions.row.actual": "Actual: {x}",
    "decisions.row.more": "+{n} more",
    "decisions.row.advance.commit": "Commit",
    "decisions.row.advance.review": "Mark reviewed",
    "decisions.confirm.delete": "Delete this decision entry?",
    "decisions.toast.created": "Decision logged",
    "decisions.toast.updated": "Decision updated",
    "decisions.toast.deleted": "Decision deleted",
    "decisions.toast.advance": "Decision moved to {x}",
    "decisions.toast.saveFail": "Save failed",
    "decisions.toast.loadFail": "Failed to load decisions",
    "decisions.toast.updateFail": "Update failed",
    "decisions.toast.deleteFail": "Delete failed",
    "decisions.form.title.new": "Log a decision",
    "decisions.form.title.edit": "Edit decision",
    "decisions.form.desc":
      "The honest pre-mortem: write what you saw, what you considered, what you chose, and why.",
    "decisions.form.context": "Context",
    "decisions.form.context.required": "Context is required",
    "decisions.form.context.ph": "What choice are you facing?",
    "decisions.form.options": "Options (one per line)",
    "decisions.form.options.ph": "Option A\nOption B\nOption C",
    "decisions.form.choice": "Choice",
    "decisions.form.choice.ph": "The option you picked",
    "decisions.form.rationale": "Rationale",
    "decisions.form.rationale.ph": "Why this option?",
    "decisions.form.expected": "Expected outcome",
    "decisions.form.expected.ph": "If this works, what does success look like in 30/90 days?",
    "decisions.form.actual": "Actual outcome",
    "decisions.form.actual.ph": "What actually happened?",
    "decisions.form.status": "Status",
    "decisions.form.submit.new": "Log",
    "decisions.status.open": "open",
    "decisions.status.committed": "committed",
    "decisions.status.reviewed": "reviewed",

    // ── Ledger
    "ledger.eyebrow": "Sandbox",
    "ledger.title": "Capital Ledger",
    "ledger.desc":
      "Every yuan in or out. The baseline at the top sets your starting net position; transactions are the diff.",
    "ledger.action.editBaseline": "Edit baseline",
    "ledger.action.new": "New transaction",
    "ledger.empty.title": "No transactions yet",
    "ledger.empty.hint": "Log an entry to start your runway.",
    "ledger.baseline.title": "Baseline",
    "ledger.baseline.subtitle": "Set on {date}",
    "ledger.baseline.starting": "Starting position",
    "ledger.baseline.starting.ph": "-50000",
    "ledger.tx.form.title": "New transaction",
    "ledger.tx.toast": "Transaction recorded",
    "ledger.tx.fail": "Failed",
    "ledger.tx.confirm.delete": "Delete this transaction?",
    "ledger.tx.toast.deleted": "Deleted",
    "ledger.tx.toast.deleteFail": "Delete failed",
    "ledger.tx.field.amount": "Amount (CNY)",
    "ledger.tx.field.date": "Date",
    "ledger.tx.field.note": "Note",
    "ledger.tx.field.category": "Category",
    "ledger.tx.field.category.ph": "rent, freelance, ad spend…",
    "ledger.baseline.form.title": "Edit baseline",
    "ledger.baseline.form.starting": "Starting position (CNY, negative if in debt)",
    "ledger.baseline.form.startingNum": "Starting position must be a number",
    "ledger.baseline.form.date": "Baseline date",
    "ledger.baseline.form.note": "Note",
    "ledger.baseline.form.note.ph": "Why this baseline?",
    "ledger.baseline.toast": "Baseline updated",
    "ledger.toast.loadFail": "Failed to load ledger",

    // ── Vault
    "vault.eyebrow": "Knowledge",
    "vault.title": "Knowledge Vault",
    "vault.desc": "Strategy documents. Authoritative copies live in markdown.",
    "vault.index": "Index",
    "vault.index.count": "{visible} of {total}",
    "vault.empty": "Vault empty",
    "vault.read.fail": "Document read failed",
    "vault.updated": "Updated {x}",
    "vault.load.fail": "Failed to load vault",
    "vault.tabs.aria": "Knowledge vault sections",
    "vault.tabs.library": "Library",
    "vault.tabs.intake": "Owner Intake",
    "vault.intake.title": "Owner Intake Desk",
    "vault.intake.subtitle": "Record facts, decisions and reflections in natural language.",
    "vault.intake.notice": "Your submission is classified by the configured analysis engine and written to the corresponding live record or domain note.",
    "vault.intake.placeholder": "Describe what happened, with amounts, dates, project names or reasoning when available...",
    "vault.intake.submit": "Audit & record",
    "vault.intake.submitting": "Recording...",
    "vault.intake.commitNote": "Submitting writes a new record immediately. Use factual, reviewable language.",
    "vault.intake.saved": "Recorded successfully",
    "vault.intake.error": "The entry could not be recorded.",
    "vault.intake.example.cashflow.label": "Cash flow",
    "vault.intake.example.cashflow": "Paid CNY 680 for cloud services today, an operating expense for the analytics project.",
    "vault.intake.example.project.label": "Project",
    "vault.intake.example.project": "Started a customer-retention pilot with CNY 5,000 committed capital, expected return 4x, medium risk, stop if no qualified leads within 30 days.",
    "vault.intake.example.decision.label": "Decision",
    "vault.intake.example.decision": "Between extending the pilot and pausing it, I chose to extend for two weeks because three qualified leads were confirmed; expect one signed trial.",
    "vault.intake.example.knowledge.label": "Reflection",
    "vault.intake.example.knowledge": "Principle: do not expand paid acquisition until repeat purchase has been demonstrated; archive this under Projects.",
    "vault.intake.target.capital_tx": "Cash-flow record",
    "vault.intake.target.projects": "Project record",
    "vault.intake.target.decisions": "Decision record",
    "vault.intake.target.github_vault": "Knowledge note",
    "vault.intake.guide.title": "High-quality input",
    "vault.intake.guide.fact.title": "Facts first",
    "vault.intake.guide.fact.desc": "State amounts, dates and names so figures can drive the dashboard.",
    "vault.intake.guide.decision.title": "Decision evidence",
    "vault.intake.guide.decision.desc": "Name options, the chosen path and the result you expect to review.",
    "vault.intake.guide.reflection.title": "Durable insight",
    "vault.intake.guide.reflection.desc": "Record principles or observations that deserve a permanent domain note.",
    "docs.section.axiom": "Axiom Core",
    "docs.section.system": "System",
    "docs.section.ops": "Operations",
    "docs.section.methods": "Methods & Measures",
    "docs.section.domains": "Nine Domains",
    "docs.product-vision.title": "Product Vision",
    "docs.product-vision.summary": "What Axiom Core solves and what it explicitly does not do.",
    "docs.decision-engine.title": "Decision Engine",
    "docs.decision-engine.summary": "Capital, return, and hard-veto rules governing every commitment.",
    "docs.dashboard-method.title": "Dashboard Measures",
    "docs.dashboard-method.summary": "Definitions, axes, interactions, and source data for the executive dashboard charts.",
    "docs.ai-agent.title": "AI Agent Charter",
    "docs.ai-agent.summary": "The CDO persona, observable inputs, and firm boundaries.",
    "docs.roadmap.title": "Roadmap",
    "docs.roadmap.summary": "Near-term, quarterly, and annual evolution path.",
    "docs.architecture.title": "System Architecture",
    "docs.architecture.summary": "Front-end and back-end tiers, local and cloud topology, and data flow.",
    "docs.data-flow.title": "Data Flow",
    "docs.data-flow.summary": "How records propagate between local storage, cloud services, and disk artifacts.",
    "docs.data-model.title": "Data Model",
    "docs.data-model.summary": "Alignment between the SQLite schema and JSON Schema.",
    "docs.security.title": "Security & Privacy",
    "docs.security.summary": "Sensitive-data rules for the public repository and credential handling.",
    "docs.deployment.title": "Deployment",
    "docs.deployment.summary": "Operator-facing deployment flow and rollback playbook.",
    "docs.operations.title": "Day-to-Day Operations",
    "docs.operations.summary": "Local services, scheduled jobs, log paths, and troubleshooting.",
    "docs.01.title": "01 · Health & Energy",
    "docs.01.summary": "Physical baseline: sleep, energy, and mood; the multiplier on all other metrics.",
    "docs.02.title": "02 · Cash Flow",
    "docs.02.summary": "Capital floor, monthly burn, and runway: the survival clock.",
    "docs.03.title": "03 · Career",
    "docs.03.summary": "Income engine: job pipeline, offer leverage, and role positioning.",
    "docs.04.title": "04 · Skills",
    "docs.04.summary": "Compounding deposits: what you learn and what you ship to prove it.",
    "docs.05.title": "05 · Projects",
    "docs.05.summary": "Capital-backed bets: active, paused, terminated, or delivered.",
    "docs.06.title": "06 · Cognition",
    "docs.06.summary": "Mental models, biases, and decision quality: upstream of every other domain.",
    "docs.07.title": "07 · Relationships",
    "docs.07.summary": "Trust network: family, partner, allies, and emotional reserves.",
    "docs.08.title": "08 · Decisions",
    "docs.08.summary": "Decision ledger: context, options, rationale, and reviewed outcomes.",
    "docs.09.title": "09 · Principles",
    "docs.09.summary": "Operating principles, hard vetoes, and irreversible commitments.",

    // ── Oracle
    "oracle.eyebrow": "Knowledge",
    "oracle.title": "Oracle Briefings",
    "oracle.desc":
      "Hand the live snapshot (capital, projects, decisions, recent transactions) to a model and ask for a brief.",
    "oracle.run": "Generate Daily Brief",
    "oracle.running": "Analyzing…",
    "oracle.prompt": "Prompt",
    "oracle.prompt.desc": "Plain English. The server prepends the live snapshot.",
    "oracle.prompt.default":
      "Given the snapshot, name the top three actions for the next 7 days, and one thing I should stop doing immediately.",
    "oracle.prompt.question": "Question",
    "oracle.model": "Model",
    "oracle.model.desc": "Picked once, remembered locally.",
    "oracle.apikey": "Gateway API Key",
    "oracle.apikey.hint": "Stored only in this browser. Overrides server env key.",
    "oracle.output": "Output",
    "oracle.output.empty": "Run a brief to see the response here.",
    "oracle.toast.ok": "Brief generated",
    "oracle.toast.timeout": "Request timed out",
    "oracle.toast.failed": "Failed",
    "oracle.empty.title": "No output yet",
    "oracle.control.title": "4SAPI Intelligence Gateway Configuration",
    "oracle.control.subtitle": "Credentials, provider & model engine",
    "oracle.control.apikey": "Gateway API Key",
    "oracle.control.apikey.placeholder": "Enter valid key from 4sapi.com (sk-…)",
    "oracle.control.apikey.hint":
      "Plain key is persisted server-side only; responses are auto-masked.",
    "oracle.control.verify": "Verify & Fetch Model Pool",
    "oracle.control.verifying": "Verifying…",
    "oracle.control.provider": "Model Provider",
    "oracle.control.provider.placeholder": "Select provider",
    "oracle.control.provider.locked": "Verify the key first",
    "oracle.control.engine": "Model Engine",
    "oracle.control.engine.placeholder": "Select engine",
    "oracle.control.engine.locked": "Select a provider first",
    "oracle.control.engine.required": "Select a model engine first.",
    "oracle.control.provider.required": "Select a provider first.",
    "oracle.control.save": "Save Configuration",
    "oracle.control.saving": "Saving…",
    "oracle.control.saved": "Configuration saved",
    "oracle.control.verified": "{count} models retrieved",
    "oracle.control.key.invalid":
      "Paste the full plain-text API key. Values containing *** are masked stubs and cannot be verified.",
    "oracle.execution.title": "Execution",
    "oracle.execution.subtitle": "Manual trigger & schedule",
    "oracle.execution.trigger": "Generate Daily Brief",
    "oracle.execution.triggering": "Calling model…",
    "oracle.execution.schedule":
      "The system audits and generates the daily brief automatically at 23:30 Asia/Shanghai.",
    "oracle.execution.scheduler.label": "Scheduler · Asia/Shanghai",
    "oracle.execution.scheduler.detail": "Cron 23:30 · automated audit & daily brief",
    "oracle.reports.title": "Reports",
    "oracle.reports.subtitle": "Latest first",
    "oracle.reports.empty": "No reports yet. Trigger one or wait for 23:30.",
    "oracle.reports.loading": "Loading…",
    "oracle.reports.kind.daily": "Daily",
    "oracle.reports.kind.manual": "Manual",
    "oracle.reports.badge.daily": "AUTO",
    "oracle.reports.badge.manual": "MANUAL",
    "oracle.status.set": "{key} · {model}",
    "oracle.status.unset": "4SAPI credentials not configured",
    "oracle.report.empty": "Select a report on the left to view its body.",
    "oracle.toast.generated": "Brief generated",
    "oracle.auto.daily.title": "Automated Daily Audit",
    "oracle.auto.daily.hint":
      "Triggers at 23:30 Asia/Shanghai · idempotent. Skips if no activity or report already exists.",
    "oracle.auto.weekly.title": "Automated Weekly Audit",
    "oracle.auto.weekly.hint":
      "Triggers Sunday 23:00 Asia/Shanghai · ISO-week scope, same idempotency rules.",
    "oracle.auto.saved": "Schedule updated",
    "oracle.execution.weekly": "Generate Weekly Brief",
    "oracle.execution.weekly.triggering": "Generating weekly…",
    "oracle.behavior.title": "Behavior-anchored",
    "oracle.behavior.detail":
      "Oracle only audits domains that saw activity today. Idle domains stay silent.",
    "oracle.reports.kind.weekly": "Weekly",
    "oracle.reports.badge.weekly": "WEEKLY",
    "oracle.reports.delete": "Delete report",
    "oracle.confirm.delete":
      "Are you sure you want to permanently erase this briefing? This action is irreversible.",
    "oracle.toast.deleted": "Report successfully removed from server persistence layer.",
    "oracle.toast.deleteFail": "Delete failed",

    // ── Settings
    "settings.eyebrow": "System",
    "settings.title": "Settings",
    "settings.desc": "Low-frequency switches for {brand}. The dashboard stays clean.",
    "settings.rows.aiKey.title": "AI key & model",
    "settings.rows.aiKey.hint": "Configure Oracle credentials in the Oracle view.",
    "settings.rows.aiKey.action": "Open Oracle",
    "settings.rows.ledger.title": "Baseline & ledger",
    "settings.rows.ledger.hint": "Edit starting position and review every transaction.",
    "settings.rows.ledger.action": "Open Ledger",
    "settings.rows.security.title": "Security playbook",
    "settings.rows.security.hint": "Sensitive-data rules for this public repo.",
    "settings.rows.security.action": "Read doc",
    "settings.rows.deploy.title": "Deployment notes",
    "settings.rows.deploy.hint": "Operator-facing deploy + rollback playbook.",
    "settings.rows.deploy.action": "Read doc",
    "settings.account.title": "Account",
    "settings.account.signout.title": "Sign out",
    "settings.account.signout.hint": "Returns you to the login screen.",
    "settings.account.signout.action": "Sign out",

    // ── Login
    "login.username": "Username",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.session": "Session lives for {x}.",
    "login.error.fallback": "Could not read auth config",
    "login.access": "Secure access",
    "login.description": "Sign in to view your capital snapshot, decision log, and protected analysis workspace.",
    "login.status": "Status",
    "login.status.waiting": "Authorization required",
    "login.session.label": "Session",
    "login.privacy": "Privacy",
    "login.privacy.value": "Protected workspace",
    "login.enter": "Enter workspace",
    "login.duration.days": "{n} days",
    "login.duration.hours": "{n} hours",
    "login.duration.minutes": "{n} minutes",
    "login.duration.seconds": "{n} seconds",
    "login.error.unavailable": "Sign-in is temporarily unavailable. Please try again later.",
    "login.error.serviceUnavailable": "The service is temporarily unavailable. Please try again later.",
    "login.error.rateLimited": "Too many requests. Please try again shortly.",
    "login.error.attemptsExceeded": "Too many failed sign-in attempts. Please try again later.",
    "login.error.invalidRequest": "Invalid sign-in request. Please try again.",
    "login.error.credentialsRequired": "Enter your username and password.",
    "login.error.notConfigured": "Sign-in is not configured. Set AXIOM_WEB_PASSWORD first.",
    "login.error.verificationRequired": "Complete the verification before trying again.",
    "login.error.verificationFailed": "Verification failed. Refresh the page and try again.",
    "login.error.locked": "This account is temporarily locked. Please try again in 10 minutes.",
    "login.error.invalidCredentials": "The username or password is incorrect.",
    "login.error.sessionFailed": "Could not create a session. Please try again later.",
  },
  zh: {
    // ── 品牌与导航
    "brand.tagline": "个人决策智能核心",
    "nav.section.sandbox": "沙盘",
    "nav.section.intelligence": "战略洞察",
    "nav.section.knowledge": "知识底座",
    "nav.section.system": "系统",
    "nav.dashboard": "控制大盘",
    "nav.dashboard.hint": "资金与跑道",
    "nav.projects": "项目沙盘",
    "nav.projects.hint": "活跃押注",
    "nav.decisions": "决策审计",
    "nav.decisions.hint": "审计日志",
    "nav.ledger": "资金账本",
    "nav.ledger.hint": "收入与支出",
    "nav.insights": "战略视界",
    "nav.insights.hint": "麦肯锡看板",
    "nav.vault": "知识底座",
    "nav.vault.hint": "战略文档",
    "nav.oracle": "AI 决策简报",
    "nav.oracle.hint": "AI 简报",
    "nav.settings": "系统设置",
    "footer.version": "v4.1 · 商业沙盘",

    // ── 应用外壳
    "shell.status.live": "在线",
    "shell.status.sync": "同步中",
    "shell.status.fail": "同步失败",
    "shell.signout": "退出登录",
    "shell.auth.checking": "校验会话中…",
    "shell.menu.open": "打开菜单",
    "shell.menu.close": "关闭菜单",
    "shell.theme.toggle.light": "切换至日间模式",
    "shell.theme.toggle.dark": "切换至夜间模式",
    "shell.lang.toggle": "切换语言",
    "shell.console": "控制台",
    "sidebar.search.placeholder": "跳转至模块…",
    "sidebar.navigation": "主导航",
    "sidebar.console": "控制台",
    "sidebar.domains": "九大领域",
    "sidebar.sqlite": "SQLite 数据表",
    "sidebar.pool": "4SAPI 模型池",
    "sidebar.build": "版本",
    "sidebar.os": "主权操作系统",

    // ── 通用
    "common.cancel": "取消",
    "common.close": "关闭",
    "common.save": "保存",
    "common.saving": "保存中…",
    "common.delete": "删除",
    "common.edit": "编辑",
    "common.create": "新建",
    "common.loading": "加载中…",
    "common.search": "搜索",
    "common.untitled": "未命名",

    // ── 控制大盘
    "dashboard.eyebrow": "指挥台",
    "dashboard.title": "资金快照",
    "dashboard.desc": "净仓位、月度净流与距离资金底线的余量。每一条录入都会纳入下一次决策。",
    "dashboard.action.log": "录入流水",
    "dashboard.net.title": "当前净仓位",
    "dashboard.net.baseline": "基线 {x} CNY · 自 {date}",
    "dashboard.net.formula": "基线 + Σ(收入) − Σ(支出)",
    "dashboard.flow.title": "月资金消耗率(30日)",
    "dashboard.flow.split": "+{inflow} / −{outflow}",
    "dashboard.runway.title": "资金跑道",
    "dashboard.runway.unit": "{x} 月",
    "dashboard.runway.unlimited": "∞ 月",
    "dashboard.runway.floor": "资金底线 {x} CNY",
    "dashboard.headroom.title": "财务安全边际",
    "dashboard.headroom.hint": "距离资金底线",
    "dashboard.status.stable": "现金流稳定",
    "dashboard.status.watch": "警戒消耗",
    "dashboard.status.risk": "触底风险",
    "dashboard.status.loading": "加载中",
    "dashboard.spark.hint": "30 日滚动净仓位。最右点为今日。",
    "dashboard.projects.title": "活跃项目",
    "dashboard.projects.subtitle": "{active} 活跃 / {total} 总数",
    "dashboard.projects.loading": "项目加载中…",
    "dashboard.projects.empty.title": "暂无活跃项目",
    "dashboard.projects.empty.hint": "进入项目沙盘新增一项投入。",
    "dashboard.projects.open": "进入沙盘",
    "dashboard.decisions.title": "待决策项",
    "dashboard.decisions.subtitle": "{open} 待决 / {total} 已记录",
    "dashboard.decisions.loading": "决策加载中…",
    "dashboard.decisions.empty.title": "暂无待决策项",
    "dashboard.decisions.empty.hint": "遇到非显而易见的选择时即时录入。",
    "dashboard.decisions.open": "打开审计日志",
    "dashboard.decisions.optsOne": "{n} 个选项 · {preview}",
    "dashboard.decisions.optsMany": "{n} 个选项 · {preview}",
    "dashboard.tx.title": "近期流水",
    "dashboard.tx.subtitle": "最近 8 条流水。完整历史在「资金账本」视图。",
    "dashboard.tx.open": "进入账本",
    "dashboard.tx.empty.title": "暂无流水",
    "dashboard.tx.empty.hint": "点击上方「录入」开始记账。",
    "dashboard.quick.title": "录入一笔流水",
    "dashboard.quick.desc": "单行录入。跑道立即重算。",
    "dashboard.quick.outflow": "流出",
    "dashboard.quick.inflow": "流入",
    "dashboard.quick.expense": "支出",
    "dashboard.quick.income": "收入",
    "dashboard.quick.amount": "金额 (CNY)",
    "dashboard.quick.amount.ph": "例如 250",
    "dashboard.quick.note": "备注",
    "dashboard.quick.note.ph": "用途？",
    "dashboard.quick.confirm": "录入",
    "dashboard.quick.submitting": "录入中…",
    "dashboard.quick.amount.invalid": "金额必须大于 0",
    "dashboard.quick.toast": "{kind} 已录入 · {x} CNY",
    "dashboard.quick.fail": "流水录入失败",
    "dashboard.floor.title": "已跌破资金底线",
    "dashboard.floor.desc": "净仓位已低于配置的资金底线 {x} CNY。在收入回升前，暂停任何增加消耗的承诺。",
    "dashboard.fetch.fail": "仪表板拉取失败",
    "dashboard.kpi.netWorth": "净资产",
    "dashboard.kpi.vs30": "较 30 日前 · {amount}",
    "dashboard.kpi.runway": "资金跑道",
    "dashboard.kpi.days": "天",
    "dashboard.kpi.positiveCashflow": "现金流为正",
    "dashboard.kpi.runwayEstimate": "预计至 {date} · 月消耗 ¥{amount}",
    "dashboard.kpi.decisions": "决策复盘结果",
    "dashboard.kpi.records": "{n} 条记录",
    "dashboard.kpi.reviewed": "{n} 条已完成复盘 · 滚动 12 个月",
    "dashboard.kpi.awaitingReview": "等待首次复盘",
    "dashboard.kpi.tokens": "4SAPI · Token 池",
    "dashboard.kpi.healthy": "健康",
    "dashboard.kpi.live": "在线",
    "dashboard.kpi.gateway": "网关",
    "dashboard.kpi.telemetryPending": "27 个密钥 · 6 家厂商 · 遥测待接入",
    "dashboard.runwayChart.title": "资金跑道预测",
    "dashboard.chart.method": "查看口径",
    "dashboard.runwayChart.empty": "请先设定基线并录入一笔流水，以生成资金跑道预测。",
    "dashboard.runwayChart.subtitle.positive": "净资本相对 {floor} CNY 资金底线的推演 · 现金流为正",
    "dashboard.runwayChart.subtitle.days": "净资本相对 {floor} CNY 资金底线的推演 · 剩余 {days} 天",
    "dashboard.runwayChart.active": "在投资本",
    "dashboard.runwayChart.cash": "净资本",
    "dashboard.runwayChart.toFloor": "距资金底线天数",
    "dashboard.runwayChart.monthlyBurn": "月度消耗",
    "dashboard.runwayChart.floor": "资金底线",
    "dashboard.runwayChart.estimate": "消耗估算",
    "dashboard.runwayChart.moving": "30 日移动估算 · 实时",
    "dashboard.runwayChart.floorLabel": "底线 · ¥{amount}",
    "dashboard.runwayChart.now": "今日 · {date}",
    "dashboard.runwayChart.actual": "实际 · {date}",
    "dashboard.runwayChart.forecast": "预测 · {date}",
    "dashboard.runwayChart.point.aria": "查看 {date} 的资金仓位",
    "dashboard.runwayChart.exhaustPositive": "∞ · 现金流为正",
    "dashboard.runwayChart.exhaustDate": "触底 {date} · {days} 天",
    "dashboard.matrix.title": "风险 × 回报定位矩阵",
    "dashboard.matrix.subtitle": "{count} 个活跃项目 · 气泡面积 = 已承诺资金 · 颜色 = 配置策略",
    "dashboard.matrix.empty": "暂无活跃资金承诺。新建项目后即可生成矩阵。",
    "dashboard.matrix.q.ideal": "优先配置",
    "dashboard.matrix.q.hero": "战略押注",
    "dashboard.matrix.q.chore": "稳健收益",
    "dashboard.matrix.q.trap": "退出复核",
    "dashboard.matrix.desc.ideal": "较低风险 × 较高回报",
    "dashboard.matrix.desc.hero": "较高风险 × 较高回报",
    "dashboard.matrix.desc.chore": "较低风险 × 较低回报",
    "dashboard.matrix.desc.trap": "较高风险 × 较低回报",
    "dashboard.matrix.axis.risk": "风险敞口",
    "dashboard.matrix.axis.roi": "预期回报",
    "dashboard.matrix.axis.low": "低",
    "dashboard.matrix.axis.high": "高",
    "dashboard.matrix.tooltip.project": "项目",
    "dashboard.matrix.tooltip.capital": "资金 · ¥{amount}",
    "dashboard.matrix.tooltip.expected": "回报 · {value}×",
    "dashboard.matrix.point.aria": "查看项目 {project}",
    "dashboard.cashflow.title": "现金流脉搏",
    "dashboard.cashflow.activity": "过去 30 天每日流入与流出 · {days} 个活跃日 · 净流 ¥{net}",
    "dashboard.cashflow.pending": "过去 30 天每日流入与流出 · 等待首笔流水",
    "dashboard.cashflow.in": "流入",
    "dashboard.cashflow.out": "流出",
    "dashboard.cashflow.empty": "过去 30 天暂无现金流活动。录入一笔收入或支出即可激活图表。",
    "dashboard.cashflow.in30": "30 日流入",
    "dashboard.cashflow.out30": "30 日流出",
    "dashboard.cashflow.net": "净流量",
    "dashboard.cashflow.largest": "最大单日净流",
    "dashboard.cashflow.bar.aria": "查看 {date} 的现金流",
    "dashboard.cashflow.selected": "选中日期 · {date}",
    "dashboard.cashflow.selected.in": "流入 +¥{value}",
    "dashboard.cashflow.selected.out": "流出 -¥{value}",
    "dashboard.cashflow.selected.net": "净流 ¥{value}",
    "dashboard.domain.title": "九大领域活动审计",
    "dashboard.domain.subtitle": "过去 30 天已记录活动 · 上限 {max}",
    "dashboard.domain.window": "30 日 · {count} 次操作",
    "dashboard.domain.missing": "未覆盖",
    "dashboard.domain.hot": "集中领域",
    "dashboard.domain.balance": "均衡度",
    "dashboard.funnel.title": "决策转化漏斗",
    "dashboard.funnel.subtitle": "信号 → 决策 → 复盘胜出 · 滚动 12 个月",
    "dashboard.funnel.observations": "已采集信号",
    "dashboard.funnel.decisions": "已作出决策",
    "dashboard.funnel.wins": "正向复盘",
    "dashboard.funnel.rate": "复盘胜率",
    "dashboard.funnel.rolling": "{wins} / {reviewed} · 滚动 12 个月",
    "dashboard.funnel.best": "最佳领域",
    "dashboard.funnel.worst": "最低领域",
    "dashboard.allocation.title": "资本配置 · MECE",
    "dashboard.allocation.subtitle": "¥{amount} · 相互独立、完全穷尽",
    "dashboard.allocation.empty": "暂无可配置的在投资本。",
    "dashboard.allocation.tag.ideal": "优先",
    "dashboard.allocation.tag.hedged": "均衡",
    "dashboard.allocation.tag.hero": "战略",
    "dashboard.allocation.tag.speculative": "投机",
    "dashboard.allocation.reserve": "流动性储备",
    "dashboard.allocation.sunk": "沉没资本",
    "dashboard.ledger.title": "近期决策台账",
    "dashboard.ledger.subtitle": "已写入 SQLite decisions 表 · 生命周期追踪已启用",
    "dashboard.ledger.filter.all": "全部",
    "dashboard.ledger.filter.open": "进行中",
    "dashboard.ledger.filter.reviewed": "已复盘",
    "dashboard.ledger.column.decision": "决策",
    "dashboard.ledger.column.domain": "领域",
    "dashboard.ledger.column.options": "选项",
    "dashboard.ledger.column.outcome": "结果",
    "dashboard.ledger.column.state": "状态",
    "dashboard.ledger.empty": "暂无决策记录",
    "dashboard.ledger.state.shipped": "正向",
    "dashboard.ledger.state.loss": "负向",
    "dashboard.ledger.state.reviewed": "已复盘",
    "dashboard.ledger.state.committed": "已承诺",
    "dashboard.ledger.state.open": "待决",
    "dashboard.principles.title": "原则审计",
    "dashboard.principles.subtitle": "本月触发记录 · 0 次违例",
    "dashboard.principles.zero": "0 次违例",
    "dashboard.principles.next": "下次审计",
    "dashboard.principles.nextDetail": "周日例行 · #03 阈值复核",
    "dashboard.principles.static": "示意数据；接入 principles 数据表后将由实时记录驱动",
    "dashboard.principles.01": "绝不动用应急储备金",
    "dashboard.principles.03": "单项目敞口不超过现金的 50%",
    "dashboard.principles.03trigger": "42% 接近上限",
    "dashboard.principles.05": "预期回报低于 3× 不投入",
    "dashboard.principles.07": "拒绝损害自主性的外包安排",
    "dashboard.principles.09": "每周至少完成一次复盘",
    "dashboard.principles.11": "债务不超过年度净现金流的三分之一",
    "dashboard.footer.release": "Axiom Core 3.0 · 主权模式",
    "dashboard.footer.tables": "SQLite · 4 张表 · ledger / projects / decisions / capital_tx",
    "dashboard.footer.gateway": "4SAPI 网关 · 多厂商",
    "dashboard.footer.nominal": "所有系统运行正常",
    "dashboard.footer.build": "构建 {date}",

    // ── 智能命令栏（自然语言录入）
    "omni.placeholder": "在此倾倒今日任意商业进展、资金流水、决策或思考，AI 自动审计归档…",
    "omni.send": "发送",
    "omni.sending": "安全审计入库中…",
    "omni.streaming": "安全审计入库中…",
    "omni.success": "数据已成功通过 AI 审计，已精准派发并持久化归档。",
    "omni.hint": "回车提交。模型自动写入 capital_tx / decisions / projects，或将思考以 Markdown 形式追加到对应领域 README。",
    "omni.toast.ok": "已归档",
    "omni.toast.fail": "审计失败",
    "omni.console.eyebrow": "系统核心指挥部",
    "omni.console.title": "一行话审计今日任何商业事件",
    "omni.console.subtitle": "资金、决策、项目、原则——直接倾倒原文，模型自动归档至正确仓位。",
    "omni.console.provider": "模型厂商",
    "omni.console.model": "解析引擎",
    "omni.console.model.default": "系统默认引擎",
    "omni.console.model.empty": "请先在 Oracle 页面配置模型",
    "omni.console.provider.empty": "选择厂商",
    "omni.console.provider.placeholder": "选择厂商",
    "omni.console.engine.empty": "当前厂商暂无可用引擎",
    "omni.console.engine.placeholder": "选择推理引擎",
    "omni.console.engine.locked": "请先选择厂商",
    "omni.console.cascade.aria": "推理引擎二级联动选择器",

    // ── 模型厂商
    "provider.anthropic": "Anthropic（Claude）",
    "provider.openai": "OpenAI（ChatGPT）",
    "provider.google": "Google（Gemini）",
    "provider.deepseek": "DeepSeek（深度求索）",
    "provider.other": "其他",

    // ── 项目
    "projects.matrix.title": "麦肯锡项目战略矩阵 (Risk-ROI Matrix)",
    "projects.matrix.subtitle": "风险 × 回报象限定位 · 气泡大小映射已投入资金",
    "projects.matrix.axisY": "预期回报倍数",
    "projects.matrix.axisX": "风险等级",
    "projects.matrix.empty": "当前沙盘内暂无活跃项目，图表底座维持内控横截面观察。",
    "projects.matrix.quadrant.q1": "明星项目",
    "projects.matrix.quadrant.q2": "投机尝试",
    "projects.matrix.quadrant.q3": "防守阵地",
    "projects.matrix.quadrant.q4": "资金陷阱",
    "projects.matrix.tooltip.roi": "预期回报",
    "projects.matrix.tooltip.committed": "已承诺",
    "projects.matrix.tooltip.spent": "已花",
    "projects.eyebrow": "沙盘",
    "projects.title": "项目沙盘",
    "projects.desc": "当前在投的商业项目。每一行呈现投资逻辑与核心假设、预期回报倍数、风险级别与硬性止损条件。",
    "projects.action.new": "新建项目",
    "projects.empty.title": "暂无项目",
    "projects.empty.hint": "点击「新建项目」录入首项投入。",
    "projects.kill": "硬性止损：",
    "projects.risk.suffix": "{level}风险",
    "projects.roi": "预期回报倍数 {x}x",
    "projects.spent": "已调配资金 {spent} / {committed} CNY",
    "projects.confirm.delete": "确认永久删除「{name}」吗？此操作不可撤销。",
    "projects.toast.created": "项目已新增",
    "projects.toast.updated": "项目已更新",
    "projects.toast.deleted": "项目已删除",
    "projects.toast.saveFail": "保存失败",
    "projects.toast.deleteFail": "删除失败",
    "projects.toast.loadFail": "项目加载失败",
    "projects.form.title.new": "新建项目",
    "projects.form.title.edit": "编辑项目",
    "projects.form.desc": "项目名称与核心假设是操作员心中的必填项；表单仅强校验名称。",
    "projects.form.name": "项目名称",
    "projects.form.name.required": "项目名称为必填项",
    "projects.form.name.ph": "例如：跨境供应链一件代发",
    "projects.form.thesis": "投资逻辑与核心假设",
    "projects.form.thesis.ph": "为何此项目能够成立。",
    "projects.form.status": "项目状态",
    "projects.form.risk": "风险等级",
    "projects.form.roi": "预期回报倍数 (×)",
    "projects.form.committed": "已调配资金 (CNY)",
    "projects.form.kill": "硬性止损红线",
    "projects.form.kill.ph": "出现什么信号即终止该项目？",
    "projects.status.active": "活跃",
    "projects.status.paused": "暂停",
    "projects.status.killed": "终止",
    "projects.status.shipped": "已交付",
    "projects.metrics.active": "活跃项目",
    "projects.metrics.live": "{count} 个在运作",
    "projects.metrics.roi": "回报倍数",
    "projects.metrics.mean": "组合均值",
    "projects.metrics.committed": "已承诺",
    "projects.metrics.allocated": "已配置 CNY",
    "projects.metrics.spent": "已消耗",
    "projects.metrics.consumed": "已消耗 CNY",
    "projects.badge.star": "优先配置",
    "projects.badge.open": "开放",
    "projects.kpi.return": "回报倍数",
    "projects.kpi.capital": "资金",
    "risk.low": "低",
    "risk.medium": "中",
    "risk.high": "高",
    "risk.extreme": "极高",

    // ── 领域（DomainSelect 基础组件）
    "domain.label": "归档至领域",
    "domain.placeholder": "暂未归档（可稍后补）",
    "domain.name.01": "健康与精力",
    "domain.name.02": "现金流",
    "domain.name.03": "职业发展",
    "domain.name.04": "技能成长",
    "domain.name.05": "项目",
    "domain.name.06": "认知",
    "domain.name.07": "关系",
    "domain.name.08": "决策",
    "domain.name.09": "原则",

    // ── 战略视界（麦肯锡看板）
    "insights.eyebrow": "战略视界",
    "insights.title": "战略洞察看板",
    "insights.desc":
      "六类关键分析一屏呈现：风险回报矩阵、资金跑道预测、资本配置审计、决策复盘漏斗、风险集中度与跨领域活动分布。",
    "insights.toast.loadFail": "战略视界数据加载失败",

    "insights.matrix.title": "风险 × 回报战略矩阵",
    "insights.matrix.subtitle": "气泡半径映射已承诺资金 · 点击直达项目明细",
    "insights.matrix.empty": "暂无项目押注，先在沙盘录入一笔后图表即上岗。",
    "insights.matrix.axisX": "风险等级",
    "insights.matrix.axisY": "预期回报倍数",
    "insights.matrix.q.star": "明星项目",
    "insights.matrix.q.speculative": "投机尝试",
    "insights.matrix.q.defensive": "防守阵地",
    "insights.matrix.q.drains": "资金陷阱",
    "insights.matrix.tooltip.roi": "回报倍数",
    "insights.matrix.tooltip.committed": "资金",

    "insights.runway.title": "资金跑道与极限收敛",
    "insights.runway.subtitle": "历史净值曲线 · 未来推演 · 绝对地板 −10 万元",
    "insights.runway.empty": "请先录入基线与一笔流水以驱动跑道曲线。",
    "insights.runway.axisX": "时间轴（历史 → 推演）",
    "insights.runway.today": "今日",
    "insights.runway.floor": "极限红线",
    "insights.runway.kpi.net": "当前净仓位",
    "insights.runway.kpi.velocity": "月度消耗速度",
    "insights.runway.kpi.survival": "存活倒计时",
    "insights.runway.survival.infinite": "∞ 天",
    "insights.runway.survival.breached": "已击穿红线",
    "insights.runway.survival.days": "剩余 {n} 天",

    "insights.allocation.title": "资本配置审计",
    "insights.allocation.subtitle": "MECE 横向堆叠条 · 已承诺 vs 已消耗",
    "insights.allocation.empty": "暂无在投资金可审计。",
    "insights.allocation.column.project": "项目",
    "insights.allocation.column.usage": "消耗占比",
    "insights.allocation.overrun": "已超支",

    "insights.funnel.title": "决策漏斗与胜率审计",
    "insights.funnel.subtitle": "待决 → 已承诺 → 已复盘 · 真实认知胜率",
    "insights.funnel.empty": "暂未录入决策记录。",
    "insights.funnel.stage.open": "待决",
    "insights.funnel.stage.committed": "已承诺",
    "insights.funnel.stage.reviewed": "已复盘",
    "insights.funnel.successRate": "复盘后胜率",
    "insights.funnel.successRate.pending": "等待首次复盘",
    "insights.funnel.foot.positive": "正向结果",
    "insights.funnel.foot.negative": "负向结果",
    "insights.funnel.foot.total": "记录总数",

    "insights.exposure.title": "资金风险集中度仪表",
    "insights.exposure.subtitle": "按风险层级汇总资本占比 · 报警阈值 40%",
    "insights.exposure.empty": "暂无可计量的在投资金。",
    "insights.exposure.threshold": "高 + 极端",
    "insights.exposure.alert.title": "集中度告警",
    "insights.exposure.alert.detail": "高与极端风险层目前占用 {pct}% 在投资本，已超 40% 重组阈值。",
    "insights.exposure.watch": "高危占比 {pct}%，已逼近 40% 重组阈值。",

    "insights.waterfall.title": "九大领域精力瀑布",
    "insights.waterfall.subtitle": "过去 30 天跨九大领域的真实操作频次",
    "insights.waterfall.empty": "尚未配置领域字典，请先维护领域列表。",
    "insights.waterfall.silent": "过去 30 天没有任何带领域标签的动作。",
    "insights.waterfall.axisX": "领域",
    "insights.waterfall.axisY": "操作频次（次）",
    "insights.waterfall.foot": "过去 {window} 天共记录到 {total} 次带领域标签的操作。",
    "insights.governance.status": "治理引擎运行中",
    "insights.governance.theme": "主题 [{mode}]",
    "insights.governance.dark": "深色",
    "insights.governance.light": "浅色",
    "insights.governance.metric.net": "净资本承载能力",
    "insights.governance.metric.audit": "合规审计率",
    "insights.governance.metric.velocity": "网关 Token 吞吐量",
    "insights.governance.metric.stable": "稳定",
    "insights.governance.metric.descriptor": "当前指标",
    "insights.governance.chart": "财务承载能力趋势",
    "insights.governance.realtimeGraph": "实时图表",
    "insights.governance.tooltip": "数值：$742,019",
    "insights.governance.register": "系统交易登记簿",
    "insights.governance.live": "实时 · 200",
    "insights.governance.column.index": "序号",
    "insights.governance.column.node": "服务节点",
    "insights.governance.column.code": "状态码",
    "insights.governance.column.latency": "延迟",
    "insights.governance.audit": "治理控制审计",
    "insights.governance.realtime": "实时",
    "insights.governance.integrity": "SQLite 结构完整性",
    "insights.governance.secure": "100% 安全",
    "insights.governance.isolation": "网络隔离过滤器",
    "insights.governance.active": "启用",
    "insights.governance.sync": "WPS / Dropbox 同步锁",
    "insights.governance.hold": "保持锁定",
    "insights.governance.journal.title": "不可篡改遥测日志",
    "insights.governance.journal.badge": "日志",
    "insights.governance.log.01": "基础链接优化校验已完成。",
    "insights.governance.log.02": "动态路由规则已通过 4SAPI 重新锚定。",
    "insights.governance.log.03": "企业同步锁已成功刷新。",
    "insights.governance.log.04": "Token 消耗曲线收敛校验已通过。",

    // ── 决策
    "decisions.eyebrow": "沙盘",
    "decisions.title": "决策审计",
    "decisions.desc": "每一次有意义的选择都成为这里的一行。记录上下文、可选项、理由与预期结果——日后诚实复盘。",
    "decisions.action.new": "录入决策",
    "decisions.bucket.open": "待决",
    "decisions.bucket.committed": "已承诺",
    "decisions.bucket.reviewed": "已复盘",
    "decisions.bucket.count.one": "{n} 条",
    "decisions.bucket.count.other": "{n} 条",
    "decisions.empty": "暂无",
    "decisions.empty.hint": "决策出现时即时录入。",
    "decisions.row.choice": "选择：{choice}",
    "decisions.row.expected": "预期：{x}",
    "decisions.row.actual": "实际：{x}",
    "decisions.row.more": "另外 {n} 项",
    "decisions.row.advance.commit": "承诺",
    "decisions.row.advance.review": "标记为已复盘",
    "decisions.confirm.delete": "删除这条决策记录？",
    "decisions.toast.created": "决策已录入",
    "decisions.toast.updated": "决策已更新",
    "decisions.toast.deleted": "决策已删除",
    "decisions.toast.advance": "决策已移至「{x}」",
    "decisions.toast.saveFail": "保存失败",
    "decisions.toast.loadFail": "决策加载失败",
    "decisions.toast.updateFail": "更新失败",
    "decisions.toast.deleteFail": "删除失败",
    "decisions.form.title.new": "录入决策",
    "decisions.form.title.edit": "编辑决策",
    "decisions.form.desc": "诚实的事前剖析：写下你所看到的、所考虑的、所选择的，以及为什么。",
    "decisions.form.context": "上下文",
    "decisions.form.context.required": "上下文为必填项",
    "decisions.form.context.ph": "你正在面临什么选择？",
    "decisions.form.options": "可选项（每行一个）",
    "decisions.form.options.ph": "选项 A\n选项 B\n选项 C",
    "decisions.form.choice": "选择",
    "decisions.form.choice.ph": "你最终选择的方案",
    "decisions.form.rationale": "理由",
    "decisions.form.rationale.ph": "为何选这一项？",
    "decisions.form.expected": "预期结果",
    "decisions.form.expected.ph": "若成立，30/90 天的成功是什么样？",
    "decisions.form.actual": "实际结果",
    "decisions.form.actual.ph": "实际发生了什么？",
    "decisions.form.status": "状态",
    "decisions.form.submit.new": "录入",
    "decisions.status.open": "待决",
    "decisions.status.committed": "已承诺",
    "decisions.status.reviewed": "已复盘",

    // ── 资金账本
    "ledger.eyebrow": "沙盘",
    "ledger.title": "资金账本",
    "ledger.desc": "每一元进出。顶部基线设定起始净仓位，流水即为差值。",
    "ledger.action.editBaseline": "编辑基线",
    "ledger.action.new": "新建流水",
    "ledger.empty.title": "暂无流水",
    "ledger.empty.hint": "录入一条以启动资金跑道。",
    "ledger.baseline.title": "基线",
    "ledger.baseline.subtitle": "设定于 {date}",
    "ledger.baseline.starting": "起始净仓位",
    "ledger.baseline.starting.ph": "-50000",
    "ledger.tx.form.title": "新建流水",
    "ledger.tx.toast": "流水已录入",
    "ledger.tx.fail": "失败",
    "ledger.tx.confirm.delete": "删除这条流水？",
    "ledger.tx.toast.deleted": "已删除",
    "ledger.tx.toast.deleteFail": "删除失败",
    "ledger.tx.field.amount": "金额 (CNY)",
    "ledger.tx.field.date": "日期",
    "ledger.tx.field.note": "备注",
    "ledger.tx.field.category": "分类",
    "ledger.tx.field.category.ph": "房租、自由职业、广告投放…",
    "ledger.baseline.form.title": "编辑基线",
    "ledger.baseline.form.starting": "起始净仓位 (CNY，负债为负)",
    "ledger.baseline.form.startingNum": "起始净仓位必须为数字",
    "ledger.baseline.form.date": "基线日期",
    "ledger.baseline.form.note": "备注",
    "ledger.baseline.form.note.ph": "这条基线的备注？",
    "ledger.baseline.toast": "基线已更新",
    "ledger.toast.loadFail": "账本加载失败",

    // ── 知识底座
    "vault.eyebrow": "知识底座",
    "vault.title": "知识底座",
    "vault.desc": "战略文档。权威副本以 Markdown 形式存档。",
    "vault.index": "索引",
    "vault.index.count": "{visible} / {total}",
    "vault.empty": "知识底座为空",
    "vault.read.fail": "文档读取失败",
    "vault.updated": "更新于 {x}",
    "vault.load.fail": "知识底座加载失败",
    "vault.tabs.aria": "知识底座栏目",
    "vault.tabs.library": "文档库",
    "vault.tabs.intake": "主人录入台",
    "vault.intake.title": "主人录入台",
    "vault.intake.subtitle": "用自然语言录入事实、决策与沉淀思考。",
    "vault.intake.notice": "提交内容将由已配置的分析引擎分类，并写入对应的实时记录或领域知识文档。",
    "vault.intake.placeholder": "描述刚刚发生的事情；如有金额、日期、项目名称、决策理由，请一并写明……",
    "vault.intake.submit": "审计并录入",
    "vault.intake.submitting": "录入中…",
    "vault.intake.commitNote": "提交后会立即写入新记录。请优先录入可核实、可复盘的信息。",
    "vault.intake.saved": "已成功录入",
    "vault.intake.error": "本次内容无法录入。",
    "vault.intake.example.cashflow.label": "资金流水",
    "vault.intake.example.cashflow": "今天支付云服务费用 680 元，属于分析项目的运营支出。",
    "vault.intake.example.project.label": "项目启动",
    "vault.intake.example.project": "启动客户留存试点项目，承诺资金 5000 元，预期回报 4 倍，中等风险；若 30 天内没有合格线索则停止。",
    "vault.intake.example.decision.label": "决策记录",
    "vault.intake.example.decision": "在延长试点和暂停之间，我选择再延长两周，因为已确认三个合格线索；预期获得一份签约试用。",
    "vault.intake.example.knowledge.label": "知识沉淀",
    "vault.intake.example.knowledge": "原则：在复购尚未得到证明前，不扩大付费获客；请归档至项目领域。",
    "vault.intake.target.capital_tx": "资金流水记录",
    "vault.intake.target.projects": "项目记录",
    "vault.intake.target.decisions": "决策记录",
    "vault.intake.target.github_vault": "知识笔记",
    "vault.intake.guide.title": "高质量录入",
    "vault.intake.guide.fact.title": "先写事实",
    "vault.intake.guide.fact.desc": "写明金额、日期与名称，数据才会准确驱动控制大盘。",
    "vault.intake.guide.decision.title": "补足决策证据",
    "vault.intake.guide.decision.desc": "注明选项、最终选择与待验证结果，便于后续复盘。",
    "vault.intake.guide.reflection.title": "沉淀长期洞见",
    "vault.intake.guide.reflection.desc": "把原则或观察写成能够长期保留的领域笔记。",
    "docs.section.axiom": "Axiom Core",
    "docs.section.system": "系统",
    "docs.section.ops": "运维",
    "docs.section.methods": "方法与口径",
    "docs.section.domains": "九大领域",
    "docs.product-vision.title": "产品愿景",
    "docs.product-vision.summary": "Axiom Core 解决的问题，以及明确不承担的事项。",
    "docs.decision-engine.title": "决策引擎",
    "docs.decision-engine.summary": "约束每一次承诺的资本、回报与硬性否决规则。",
    "docs.dashboard-method.title": "控制大盘图表口径",
    "docs.dashboard-method.summary": "控制大盘图表的坐标定义、交互方式与数据来源。",
    "docs.ai-agent.title": "AI 智能体章程",
    "docs.ai-agent.summary": "CDO 角色、可观察输入与明确边界。",
    "docs.roadmap.title": "路线图",
    "docs.roadmap.summary": "近期、季度与年度演进路径。",
    "docs.architecture.title": "系统架构",
    "docs.architecture.summary": "前后端分层、本地与云端拓扑，以及数据流向。",
    "docs.data-flow.title": "数据流",
    "docs.data-flow.summary": "记录在本地、云端与磁盘制品之间的传递方式。",
    "docs.data-model.title": "数据模型",
    "docs.data-model.summary": "SQLite 模式与 JSON Schema 的一致性设计。",
    "docs.security.title": "安全与隐私",
    "docs.security.summary": "公开仓库中的敏感数据规则与凭据处理规范。",
    "docs.deployment.title": "部署",
    "docs.deployment.summary": "面向运维者的部署流程与回滚手册。",
    "docs.operations.title": "日常运维",
    "docs.operations.summary": "本地服务、计划任务、日志路径与故障排查。",
    "docs.01.title": "01 · 健康与精力",
    "docs.01.summary": "身体基线：睡眠、精力与情绪；它们会放大其他指标的表现。",
    "docs.02.title": "02 · 现金流",
    "docs.02.summary": "资金底线、月度消耗与跑道：生存时钟。",
    "docs.03.title": "03 · 职业发展",
    "docs.03.summary": "收入引擎：求职管道、报价杠杆与角色定位。",
    "docs.04.title": "04 · 技能成长",
    "docs.04.summary": "复利积累：持续学习的内容，以及证明能力的交付成果。",
    "docs.05.title": "05 · 项目",
    "docs.05.summary": "资本支持的投入：活跃、暂停、终止或已交付。",
    "docs.06.title": "06 · 认知",
    "docs.06.summary": "心智模型、偏差与决策质量：其他领域的上游。",
    "docs.07.title": "07 · 关系",
    "docs.07.summary": "信任网络：家人、伴侣、盟友与情绪储备。",
    "docs.08.title": "08 · 决策",
    "docs.08.summary": "决策台账：情境、选项、理由与复盘结果。",
    "docs.09.title": "09 · 原则",
    "docs.09.summary": "行动原则、硬性否决项与不可逆承诺。",

    // ── Oracle
    "oracle.eyebrow": "知识底座",
    "oracle.title": "AI 决策简报",
    "oracle.desc": "把实时沙盘快照（资金、项目、决策、近期流水）交给模型，请求一份决策简报。",
    "oracle.run": "手动生成今日简报",
    "oracle.running": "分析中…",
    "oracle.prompt": "提示",
    "oracle.prompt.desc": "用普通语言。服务端会在你的提示前拼接实时沙盘快照。",
    "oracle.prompt.default": "根据快照，给出未来 7 天最重要的三个动作；并指出一件我应立即停止的事。",
    "oracle.prompt.question": "问题",
    "oracle.model": "模型",
    "oracle.model.desc": "选一次，浏览器记住。",
    "oracle.apikey": "网关 API 密钥",
    "oracle.apikey.hint": "仅存于当前浏览器。会覆盖服务端环境变量。",
    "oracle.output": "输出",
    "oracle.output.empty": "生成简报后，结果显示于此。",
    "oracle.toast.ok": "简报已生成",
    "oracle.toast.timeout": "请求超时",
    "oracle.toast.failed": "失败",
    "oracle.empty.title": "暂无输出",
    "oracle.control.title": "4SAPI 商业智能网关配置",
    "oracle.control.subtitle": "凭据、厂商与模型引擎",
    "oracle.control.apikey": "网关 API 密钥",
    "oracle.control.apikey.placeholder": "请输入来自 4sapi.com 的有效密钥 (sk-…)",
    "oracle.control.apikey.hint": "明文密钥仅在服务端持久化；返回前自动脱敏。",
    "oracle.control.verify": "验证并拉取模型库",
    "oracle.control.verifying": "验证中…",
    "oracle.control.provider": "模型厂商",
    "oracle.control.provider.placeholder": "选择厂商",
    "oracle.control.provider.locked": "请先验证密钥",
    "oracle.control.engine": "模型引擎",
    "oracle.control.engine.placeholder": "选择引擎",
    "oracle.control.engine.locked": "请先选择厂商",
    "oracle.control.engine.required": "请先选择模型引擎。",
    "oracle.control.provider.required": "请先选择模型厂商。",
    "oracle.control.save": "固化配置",
    "oracle.control.saving": "保存中…",
    "oracle.control.saved": "配置已写入",
    "oracle.control.verified": "已拉取 {count} 个可用模型",
    "oracle.control.key.invalid": "请粘贴完整的明文 API 密钥（含 *** 表示脱敏值，无法用于验证）。",
    "oracle.execution.title": "执行面板",
    "oracle.execution.subtitle": "手动触发与计划任务",
    "oracle.execution.trigger": "手动生成今日简报",
    "oracle.execution.triggering": "调用模型中…",
    "oracle.execution.schedule": "系统于每日 23:30（上海时间）自动审计并生成今日简报。",
    "oracle.execution.scheduler.label": "调度器 · 上海时区",
    "oracle.execution.scheduler.detail": "Cron 23:30 · 自动审计并写入日报",
    "oracle.reports.title": "历史报告",
    "oracle.reports.subtitle": "最新在前",
    "oracle.reports.empty": "尚无报告。请手动触发，或等待 23:30 自动生成。",
    "oracle.reports.loading": "加载中…",
    "oracle.reports.kind.daily": "日报",
    "oracle.reports.kind.manual": "手动",
    "oracle.reports.badge.daily": "自动",
    "oracle.reports.badge.manual": "手动",
    "oracle.status.set": "{key} · {model}",
    "oracle.status.unset": "尚未配置 4SAPI 凭据",
    "oracle.report.empty": "在左侧选择一份报告以查看正文。",
    "oracle.toast.generated": "简报已生成",
    "oracle.auto.daily.title": "每日自动审计简报",
    "oracle.auto.daily.hint": "上海时间 23:30 触发 · 基于行为感知自动运行；当天无业务行为或已存在日报时自动跳过。",
    "oracle.auto.weekly.title": "每周自动审计周报",
    "oracle.auto.weekly.hint": "每周日 23:00 触发 · 跨维度趋势深度整合，遵循相同的幂等规则。",
    "oracle.auto.saved": "调度已更新",
    "oracle.execution.weekly": "手动生成本周周报",
    "oracle.execution.weekly.triggering": "生成周报中…",
    "oracle.behavior.title": "基于行为的审计",
    "oracle.behavior.detail": "AI 简报仅评估当天有归档动作的领域；无记录的领域不会被纳入评价。",
    "oracle.reports.kind.weekly": "周报",
    "oracle.reports.badge.weekly": "周报",
    "oracle.reports.delete": "删除该报告",
    "oracle.confirm.delete": "确认永久删除这份审计简报吗？此操作不可撤销。",
    "oracle.toast.deleted": "报告已安全从服务器持久化层移除。",
    "oracle.toast.deleteFail": "删除失败",

    // ── 系统设置
    "settings.eyebrow": "系统",
    "settings.title": "系统设置",
    "settings.desc": "{brand} 的低频开关。仪表板保持干净。",
    "settings.rows.aiKey.title": "AI 密钥与模型",
    "settings.rows.aiKey.hint": "在 AI 决策简报视图配置网关凭据。",
    "settings.rows.aiKey.action": "进入 AI 简报",
    "settings.rows.ledger.title": "基线与账本",
    "settings.rows.ledger.hint": "编辑起始净仓位并复核每一笔流水。",
    "settings.rows.ledger.action": "进入账本",
    "settings.rows.security.title": "安全手册",
    "settings.rows.security.hint": "公开仓库下的敏感数据规则。",
    "settings.rows.security.action": "查看文档",
    "settings.rows.deploy.title": "部署说明",
    "settings.rows.deploy.hint": "运维视角的部署与回滚手册。",
    "settings.rows.deploy.action": "查看文档",
    "settings.account.title": "账户",
    "settings.account.signout.title": "退出登录",
    "settings.account.signout.hint": "退出后返回登录页。",
    "settings.account.signout.action": "退出登录",

    // ── 登录
    "login.username": "账号",
    "login.password": "密码",
    "login.submit": "登录",
    "login.session": "会话有效期 {x}。",
    "login.error.fallback": "无法读取登录配置",
    "login.access": "安全访问",
    "login.description": "登录后查看资金快照、决策台账与受保护的分析工作区。",
    "login.status": "状态",
    "login.status.waiting": "等待授权",
    "login.session.label": "会话",
    "login.privacy": "隐私",
    "login.privacy.value": "工作区受保护",
    "login.enter": "进入工作区",
    "login.duration.days": "{n} 天",
    "login.duration.hours": "{n} 小时",
    "login.duration.minutes": "{n} 分钟",
    "login.duration.seconds": "{n} 秒",
    "login.error.unavailable": "登录暂时不可用，请稍后重试。",
    "login.error.serviceUnavailable": "服务暂时不可用，请稍后重试。",
    "login.error.rateLimited": "请求频率过高，请稍后再试。",
    "login.error.attemptsExceeded": "登录失败次数过多，请稍后再试。",
    "login.error.invalidRequest": "登录请求格式错误，请重试。",
    "login.error.credentialsRequired": "请输入账号和密码。",
    "login.error.notConfigured": "登录未配置，请先设置 AXIOM_WEB_PASSWORD。",
    "login.error.verificationRequired": "人机验证未完成，请等待校验后重试。",
    "login.error.verificationFailed": "人机验证失败，请刷新页面后重试。",
    "login.error.locked": "账号已被临时锁定，请 10 分钟后重试。",
    "login.error.invalidCredentials": "账号或密码不正确。",
    "login.error.sessionFailed": "会话创建失败，请稍后重试。",
  },
};

function format(template: string, vars?: Replacements): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, key: string) => {
    if (key in vars) {
      const value = vars[key];
      return value === undefined || value === null ? "" : String(value);
    }
    return m;
  });
}

type LangCtx = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
  t: (key: string, vars?: Replacements) => string;
};

const LangContext = createContext<LangCtx>({
  lang: "en",
  setLang: () => undefined,
  toggle: () => undefined,
  t: (key) => key,
});

function readStoredLang(): Lang | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "en" || value === "zh" ? value : null;
  } catch {
    return null;
  }
}

function browserDefault(): Lang {
  if (typeof navigator === "undefined") return "en";
  const navLang = (navigator.language || "en").toLowerCase();
  return navLang.startsWith("zh") ? "zh" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang() ?? browserDefault());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.setAttribute("lang", lang === "zh" ? "zh-CN" : "en");
  }, [lang]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);
  const toggle = useCallback(() => setLangState((prev) => (prev === "en" ? "zh" : "en")), []);

  const t = useCallback(
    (key: string, vars?: Replacements) => {
      const dict = DICT[lang] || DICT.en;
      const template = dict[key] ?? DICT.en[key] ?? key;
      return format(template, vars);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, setLang, toggle, t]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useI18n(): LangCtx {
  return useContext(LangContext);
}

export function useT(): LangCtx["t"] {
  return useContext(LangContext).t;
}
